import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store'
import { IGeneralMapState } from './general-map.reducer'
import { StoreKeysEnum } from '../../../../../store/store-keys'
import { Feature } from 'ol'
import cloneDeep from 'lodash/cloneDeep'

export const selectGeneralMapFeature = createFeatureSelector<Readonly<IGeneralMapState>>(StoreKeysEnum.GeneralMapComponent)

export const selectGeneralMapState = (mapId: string): MemoizedSelector<IGeneralMapState, any> => createSelector(
    selectGeneralMapFeature,
    feature => {
        return feature[mapId]
    },
)

export const selectGeneralMapDrawingState = (mapId: string): any => createSelector(
    selectGeneralMapState(mapId),
    state => state?.parcelToGroupDrawing?.drawInfoboxDrawingState,
)

export const selectGeneralMapSplitInfoboxState = (mapId: string): any => createSelector(
    selectGeneralMapState(mapId),
    state => state?.parcelToGroupDrawing?.splitInfoboxState,
)

export const selectGeneralMapDrawingModifyingActive = (mapId: string): any => createSelector(
    selectGeneralMapDrawingState(mapId),
    selectGeneralMapSplitInfoboxState(mapId),
    (drawInfoboxDrawingState, splitInfoboxState) => (drawInfoboxDrawingState  !== null || splitInfoboxState  !== null),
)

export const selectGeneralMapLayersConfig = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    state => (state ? state.layersConfig : null),
)

// export const selectGeneralMapMapAndLayersConfig = (mapId: string): any => createSelector(
//     selectGeneralMapMap(mapId),
//     selectGeneralMapLayersConfig(mapId),
//     (map, layersConfig) => ({map, layersConfig}),
// )

export const selectGeneralMapShowedBoxAndInfo = (mapId: string): MemoizedSelector<any,  any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => ({
        currentType: state?.showedInfoBox,
        currentFeatureInfo: state?.currentFeatureInfo,
    }),
)

export const selectGeneralMapToggleMeasure = (mapId: string): MemoizedSelector<any,  any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => ({
        currentType: state?.showedInfoBox,
        currentFeatureInfo: state?.currentFeatureInfo,
        layersOutsideSelector: state?.layersOutsideSelector,
    }),
)

export const selectGeneralMapShowedInfoBox = (mapId: string): MemoizedSelector<any,  any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state ? state.showedInfoBox : null),
)

// export const selectGeneralMapMeasureDrawing = (mapId: string): OutputSelector<object, any, (res: Readonly<any>) => any> => createSelector(
//     selectGeneralMapState(mapId),
//     (state: IGeneralMapState) => (state ? state.measureDrawing : null),
// )
export const selectGeneralMapMeasuredArea = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state ? state.measuredArea : null),
)
export const selectGeneralMapMeasuredLength = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state ? state.measuredLength : null),
)

export const selectGeneralMapMeasuredCoordinates = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state ? state.measuredCoordinates : null),
)

export const selectGeneralMapSaveLayersVisibility = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state ? state.saveLayersVisibility : null),
)

export const selectGeneralMapParcelToGroupDrawing = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.parcelToGroupDrawing),
)

export const selectFixedLayersVisibilityFromMapZoom = (mapId: string): any => createSelector(
    selectGeneralMapLayersConfig(mapId),
    selectGeneralMapParcelToGroupDrawing(mapId),
    (layersConfig, parcelToGroupDrawing) => ({ layersConfig, parcelToGroupDrawing }),
)

export const selectGeneralMapParcelToGroupDrawingSplitInfoboxState = (mapId: string): any => createSelector(
    selectGeneralMapParcelToGroupDrawing(mapId),
    (parcelToGroupDrawing) => parcelToGroupDrawing?.splitInfoboxState
)

export const selectGeneralMapParcelToGroupDrawingDrawInfoboxDrawingCoordinatesLength = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapParcelToGroupDrawing(mapId),
    (parcelToGroupDrawing) => parcelToGroupDrawing?.drawInfoboxDrawingCoordinatesLength
)

export const selectGeneralMapSaveLayersConfig = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    state => ({ saveLayersVisibility: state?.saveLayersVisibility, storagePrefix: state?.storagePrefix, layersConfig: state?.layersConfig }),
)

export const selectGeneralMapParcelContractEndingDate = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    state => (state?.parcelContractEndingDate),
)

export const selectGeneralMapPagePrintHeading = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.pagePrintHeading),
)

export const selectGeneralMapShowedLpisUserSelection = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.showedLpisUserSelection || null),
)

export const selectGeneralMapShowedLpisEndingContract = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.showedLpisEndingContract || null),
)

export const selectGeneralMapShowedParcelSelectByShapeDrawing = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.showedParcelSelectByShapeDrawing || null),
)

export const selectGeneralMapShowedParcelSelectByAddArea= (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.showedParcelSelectByAddArea || null),
)

export const selectGeneralMapShowedParcelSplitting = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.showedParcelSplitting || null),
)

export const selectGeneralMapCollapsed = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.collapsed),
)

export const selectLpisUserSelectionCollapsed = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.lpisUserSelectionCollapsed),
)

export const selectLpisEndingContractCollapsed = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.lpisEndingContractCollapsed),
)

export const selectParcelSelectByShapeDrawingCollapsed = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.parcelSelectByShapeDrawingCollapsed),
)

export const selectParcelSelectByAddAreaCollapsed = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.parcelSelectByAddAreaCollapsed),
)

export const selectParcelSplittingCollapsed = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.parcelSplittingCollapsed),
)

// export const selectGeneralMapDetailFeature = (mapId: string): MemoizedSelector<any, any> => createSelector(
//     selectGeneralMapState(mapId),
//     (state: IGeneralMapState) => (state?.detailFeature),
// )

export const selectGeneralMapBacklink = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.backlink),
)

export const selectGeneralMapHeader = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.header),
)

export const selectGeneralMapParcelListShowAddonInformation = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.parcelListShowAddonInformation),
)

export const selectGeneralMapFeatures = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.features),
)


export const selectGeneralMapPadding = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.padding),
)
export const selectGeneralMapZoomedId = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => (state?.zoomedId),
)
export const selectGeneralMapPaddingAndZoomedId = (mapId: string): any => createSelector(
    selectGeneralMapPadding(mapId),
    selectGeneralMapZoomedId(mapId),
    (padding, zoomedId) => ({padding, zoomedId}),
)

export const selectGeneralMapFeaturesChecked = (mapId: string): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => {
        const updatedFeatures = cloneDeep(state?.features)
        if (Array.isArray(state?.features) && state?.features?.length) {
            updatedFeatures.forEach((feature: Feature<any>) => {
                if (feature.get('checked') === null || feature.get('checked') === undefined) {
                    // silent=true: cloned feature still carries listener refs to original VectorSource;
                    // dispatching propertychange triggers RBush.update on a feature not in items_ → crash (Sentry CA-ASSETS-FRONTEND-5D5)
                    feature.set('checked', true, true)
                }
            })
        }
        return updatedFeatures
    },
)

export const selectGeneralMapshowAddonInformationAble = (mapId: string, limit: number): MemoizedSelector<any, any> => createSelector(
    selectGeneralMapState(mapId),
    (state: IGeneralMapState) => Array.isArray(state?.features) && !(state?.features?.length > limit)
)

// ==> BarterPassReceiveParcelsMapComponent
export const selectBarterPassReceiveParcelsMapState = (mapId: string): any => createSelector(
    selectGeneralMapShowedInfoBox(mapId),
    selectGeneralMapShowedLpisUserSelection(mapId),
    selectGeneralMapShowedLpisEndingContract(mapId),
    selectGeneralMapPagePrintHeading(mapId),
    (showedInfoBox, showedLpisUserSelection, showedLpisEndingContract, pagePrintHeading) => ({
        showedInfoBox,
        showedLpisUserSelection,
        showedLpisEndingContract,
        pagePrintHeading,
    }),
)

// ==> MapInfoBoxComponent
export const selectGeneralMapInfoBoxState = (mapId: string): any => createSelector(
    // selectGeneralMapShowedInfoBox(mapId),
    // selectGeneralMapShowedLpisUserSelection(mapId),
    // selectGeneralMapShowedLpisEndingContract(mapId),
    selectGeneralMapCollapsed(mapId),
    selectLpisUserSelectionCollapsed(mapId),
    selectLpisEndingContractCollapsed(mapId),
    selectParcelSelectByShapeDrawingCollapsed(mapId),
    selectParcelSelectByAddAreaCollapsed(mapId),
    selectParcelSplittingCollapsed(mapId),
    (
        // showedInfoBox,
        // showedLpisUserSelection,
        // showedLpisEndingContract,
        collapsed, lpisUserSelectionCollapsed, lpisEndingContractCollapsed, parcelSelectByShapeDrawingCollapsed, parcelSelectByAddAreaCollapsed, parcelSplittingCollapsed) => ({
        // showedInfoBox,
        // showedLpisUserSelection,
        // showedLpisEndingContract,
        collapsed,
        lpisUserSelectionCollapsed,
        lpisEndingContractCollapsed,
        parcelSelectByShapeDrawingCollapsed,
        parcelSelectByAddAreaCollapsed,
        parcelSplittingCollapsed,
    })
)

// ==> MapInfoBoxHeaderComponent
export const selectGeneralMapInfoBoxHeaderState  = (mapId: string): any => createSelector(
    selectGeneralMapBacklink(mapId),
    selectGeneralMapHeader(mapId),
    // selectGeneralMapDetailFeature(mapId),
    selectGeneralMapCollapsed(mapId),
    (backlink, header, collapsed) => ({
        backlink,
        header,
        // item: detailFeature?.values_,
        collapsed,
        // fbId: detailFeature,
    })
)

// ==> MapInfoBoxMeasureContentComponent
export const selectGeneralMapMeasure  = (mapId: string): any => createSelector(
    selectGeneralMapMeasuredArea(mapId),
    // selectGeneralMapMeasureDrawing(mapId),
    selectGeneralMapMeasuredLength(mapId),
    (measuredArea, measuredLength) => ({measuredArea, measuredLength})
)

// ==> Map
export const selectGeneralMapAllShowed = (mapId: string): any => createSelector(
    selectGeneralMapShowedInfoBox(mapId),
    selectGeneralMapShowedLpisUserSelection(mapId),
    selectGeneralMapShowedLpisEndingContract(mapId),
    selectGeneralMapShowedParcelSelectByShapeDrawing(mapId),
    selectGeneralMapShowedParcelSelectByAddArea(mapId),
    selectGeneralMapShowedParcelSplitting(mapId),
    (showedInfoBox, showedLpisUserSelection, showedLpisEndingContract, showedParcelSelectByShapeDrawing, showedParcelSelectByAddArea, showedParcelSplitting) => ({
        showedInfoBox,
        showedLpisUserSelection,
        showedLpisEndingContract,
        showedParcelSelectByShapeDrawing,
        showedParcelSelectByAddArea,
        showedParcelSplitting,
    })
)
export const selectGeneralMapAllCollapsed  = (mapId: string): any => createSelector(
    selectGeneralMapCollapsed(mapId),
    selectLpisUserSelectionCollapsed(mapId),
    selectLpisEndingContractCollapsed(mapId),
    selectParcelSelectByShapeDrawingCollapsed(mapId),
    selectParcelSelectByAddAreaCollapsed(mapId),
    selectParcelSplittingCollapsed(mapId),
    (collapsed, lpisUserSelectionCollapsed, lpisEndingContractCollapsed, parcelSelectByShapeDrawingCollapsed, parcelSelectByAddAreaCollapsed, parcelSplittingCollapsed) => ({
        collapsed,
        lpisUserSelectionCollapsed,
        lpisEndingContractCollapsed,
        parcelSelectByShapeDrawingCollapsed,
        parcelSelectByAddAreaCollapsed,
        parcelSplittingCollapsed,
    })
)
